import { useCallback } from "react";
import { toast } from "react-toastify";

const HTTP_STATUS_MESSAGES = {
  400: "Bad Request",
  401: "Unauthorized",
  403: "Forbidden — you don't have permission to access this resource",
  404: "Not Found",
  405: "Method Not Allowed",
  408: "Request Timeout",
  409: "Conflict",
  422: "Unprocessable Entity",
  429: "Too Many Requests",
  500: "Internal Server Error",
  502: "Bad Gateway",
  503: "Service Unavailable",
  504: "Gateway Timeout",
};

const extractErrorDetails = (error) => {
  const responseData = error?.response?.data;
  const status =
    responseData?.status ??
    error?.response?.status ??
    error?.code;

  const numericStatus = parseInt(status, 10);

  const apiMessage =
    responseData?.title ??
    responseData?.ResponseStatus?.Message ??
    responseData?.detail ??
    (typeof responseData === "string" && !responseData.startsWith("<")
      ? responseData
      : null);

  // statusText is often empty in modern browsers — use known HTTP messages as fallback
  const fallbackMessage =
    (!isNaN(numericStatus) && HTTP_STATUS_MESSAGES[numericStatus]) ||
    (error?.response?.statusText || null) ||
    error?.message ||
    "An unexpected error occurred";

  const message = apiMessage || fallbackMessage;

  return { status, message };
};

export const useError = () => {
  const handleError = useCallback((error) => {
    if (!error) return;

    // Ignore cancelled requests
    if (error?.code === "ERR_CANCELED") return;

    const { status, message } = extractErrorDetails(error);
    const numericStatus = parseInt(status, 10);
    const label = status ? `${status}: ${message}` : message;

    if (!isNaN(numericStatus) && numericStatus >= 500) {
      toast.error(label);
    } else if (!isNaN(numericStatus) && numericStatus >= 400) {
      toast.warning(label);
    } else {
      toast.error(label);
    }
  }, []);

  return { handleError };
};
